import { KeyboardEvent, RefObject, useCallback, useMemo, useState } from 'react';
import { ExpressionVariable } from './expression/ExpressionBuilder';

/** Présentations proposées, après le choix de la variable, quand le tunnel ne déclare pas d'unité. */
export const FORMATS: Array<{ suffixe: string; libelle: string }> = [
  { suffixe: '', libelle: 'Nombre' },
  { suffixe: '|euros', libelle: 'Euros' },
  { suffixe: '|pourcent', libelle: 'Pourcentage' },
  { suffixe: '|m2', libelle: 'm²' },
  { suffixe: '|ouinon', libelle: 'Oui / non' },
];

const MAX_SUGGESTIONS = 8;

/** Rectangle du curseur en coordonnées de la fenêtre : la liste, rendue hors du cadre de l'éditeur, s'y ancre. */
export interface Position {
  top: number;
  bottom: number;
  left: number;
}

interface Saisie {
  /** Texte tapé après « {{ », filtre de la liste. */
  query: string;
  /** Nœud texte et bornes de « {{query », remplacées par le jeton. */
  node: Text;
  start: number;
  end: number;
  position: Position;
}

type Etape =
  | { kind: 'variables'; saisie: Saisie }
  | { kind: 'format'; saisie: Saisie; variable: ExpressionVariable };

/**
 * Autocomplétion des jetons {{CODE}} dans un `contentEditable` : taper « {{ » ouvre, au
 * curseur, la liste des variables du tunnel filtrée au fil de la frappe sur le code et le
 * libellé. Une variable avec unité s'insère telle quelle ({{CODE}}, formatée par l'unité à la
 * génération) ; sans unité, une seconde liste courte demande la présentation, Nombre par
 * défaut. Entrée, Tab ou clic valident ; Échap referme sans rien insérer.
 */
export const useVariablesAutocomplete = (
  editorRef: RefObject<HTMLDivElement>,
  variables: ExpressionVariable[] | undefined,
  onChanged: () => void
) => {
  const [etape, setEtape] = useState<Etape | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const variablesFiltrees = useMemo(() => {
    if (!etape || etape.kind !== 'variables') return [];
    const query = etape.saisie.query.toLowerCase();
    return (variables ?? [])
      .filter(
        (variable) =>
          variable.code.toLowerCase().includes(query) || variable.libelle.toLowerCase().includes(query)
      )
      .slice(0, MAX_SUGGESTIONS);
  }, [etape, variables]);

  /** Après chaque saisie : cherche « {{texte » juste avant le curseur dans le nœud courant. */
  const detect = useCallback(() => {
    if (!variables || variables.length === 0) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) {
      setEtape(null);
      return;
    }
    const range = selection.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE || !editorRef.current?.contains(node)) {
      setEtape(null);
      return;
    }
    const text = (node as Text).data.slice(0, range.startOffset);
    const match = /\{\{([^{}]*)$/.exec(text);
    if (!match) {
      setEtape(null);
      return;
    }
    // Un curseur en début de nœud vide n'a pas de rectangle : on retombe sur l'éditeur.
    let caret = range.getBoundingClientRect();
    if (caret.width === 0 && caret.height === 0 && editorRef.current) {
      caret = editorRef.current.getBoundingClientRect();
    }
    const position: Position = { top: caret.top, bottom: caret.bottom, left: caret.left };
    setEtape({
      kind: 'variables',
      saisie: { query: match[1], node: node as Text, start: match.index, end: range.startOffset, position },
    });
    setActiveIndex(0);
  }, [editorRef, variables]);

  const inserer = useCallback(
    (saisie: Saisie, jeton: string) => {
      const { node, start, end } = saisie;
      const data = node.data;
      node.data = data.slice(0, start) + jeton + ' ' + data.slice(end);
      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        range.setStart(node, start + jeton.length + 1);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      setEtape(null);
      onChanged();
    },
    [onChanged]
  );

  const choisirVariable = useCallback(
    (variable: ExpressionVariable) => {
      if (!etape) return;
      if (variable.unite) {
        inserer(etape.saisie, `{{${variable.code}}}`);
        return;
      }
      setEtape({ kind: 'format', saisie: etape.saisie, variable });
      setActiveIndex(0);
    },
    [etape, inserer]
  );

  const choisirFormat = useCallback(
    (suffixe: string) => {
      if (!etape || etape.kind !== 'format') return;
      inserer(etape.saisie, `{{${etape.variable.code}${suffixe}}}`);
    },
    [etape, inserer]
  );

  const close = useCallback(() => setEtape(null), []);

  const nombreEntrees = etape?.kind === 'format' ? FORMATS.length : variablesFiltrees.length;

  /** Navigation clavier dans la liste ; renvoie vrai si la touche a été consommée. */
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): boolean => {
      if (!etape || nombreEntrees === 0) return false;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setActiveIndex((index) => (index + 1) % nombreEntrees);
          return true;
        case 'ArrowUp':
          event.preventDefault();
          setActiveIndex((index) => (index - 1 + nombreEntrees) % nombreEntrees);
          return true;
        case 'Enter':
        case 'Tab':
          event.preventDefault();
          if (etape.kind === 'format') choisirFormat(FORMATS[activeIndex].suffixe);
          else choisirVariable(variablesFiltrees[activeIndex]);
          return true;
        case 'Escape':
          event.preventDefault();
          close();
          return true;
        default:
          return false;
      }
    },
    [activeIndex, choisirFormat, choisirVariable, close, etape, nombreEntrees, variablesFiltrees]
  );

  return {
    isOpen: etape !== null && nombreEntrees > 0,
    etape: etape?.kind ?? null,
    position: etape?.saisie.position ?? null,
    variable: etape?.kind === 'format' ? etape.variable : null,
    variables: variablesFiltrees,
    activeIndex,
    setActiveIndex,
    detect,
    choisirVariable,
    choisirFormat,
    close,
    onKeyDown,
  };
};
